/* eslint-disable complexity */
import { Text } from "@imminently/react/ui/core/text";
import global, { formatUtc2HospitalDate } from "@/global";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import { Box, Stack, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, Alert } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import { parseISO } from "date-fns";
import * as _ from "lodash";
import { useCallback, useState } from "react";
import { useSettingsContext } from "@/pages/settings/SettingsProvider";
import { useEditObservationModal } from "@/modals/addObservation";
import JsonEditor from "@/components/JsonEditor";

import { TimeIcon } from "@/assets/icons";
import { BulletPoint } from "@/components/BulletPoint";
import ObservationSummary from "@/components/ObservationSummary/ObservationSummary";
import type {
  Activity,
  ActivityFilters,
  Note,
  Observation,
  PatientEncounter,
  Practitioners,
  TimelineItemBase,
} from "@/resources/types";
import { css } from "@/theme";
import { filterTimelineItem as filterItem } from "./timelineFilters";

const roundedChip = css`
  font-family: "Lato";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  display: inline;
  padding: 0.15rem 0.4rem;
  border-radius: 1rem;
  height: 100%;
`;
const styles = {
  timeline: {
    [`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 },
  },
  roleBox: css`
    ${roundedChip}
    background-color: #b8defc;

  `,
  typeBox: css`
    ${roundedChip}
    background-color: #aff1ef;

  `,
  connector: css`
    margin: -11.5px 0;
    background: repeating-linear-gradient(180deg, #c3c8de 0 2px, #0000 0 4px);
  `,
  dot: css`
    border-color: #354176;
    padding: 5px;
  `,
};

type Event = {
  id: string;
  timestamp: string;
  correlationId: string;
  message: string;
  messageType: string;
};

type EventTimelineItem = Event & {
  type: "event" | "encounter" | "outbound" | "inbound";
  role: string;
  created: string;
  createdByName: string;
  createdByRole: string;
  text: string;
  actingAsName?: string;
};

type TimelineItem =
  | (Note & { type: "simpleAndNote" | "communication" })
  | (TimelineItemBase & { type: "simple" | "create" | "status"; role: string })
  | (Observation & { type: "observation"; role: string; summary?: React.ReactNode; observationId?: string; isLatestForObservation?: boolean })
  | EventTimelineItem;

type PatientTimelineProps = {
  encounter: PatientEncounter;
  filters: ActivityFilters;
  events: Event[];
};

const formatTime = (created: string) => {
  // const createdDate = parseISO(created);
  // const now = new Date();

  // const minutesAgo = 60;
  // const oneDay = 24 * 60 * 60 * 1000;
  // const timeDiff = now.getTime() - createdDate.getTime();

  // // Customer wants full time to always appear. Leaving old code in case they change their mind
  // /*if (timeDiff < minutesAgo * 60 * 1000) {
  //   return formatDistanceStrict(createdDate, now) + " ago";
  // } else if (timeDiff < oneDay) {
  //   return format(createdDate, "HH:mm");
  // } else {*/
  //   return format(createdDate, "EEEE d MMMM - HH:mm");
  // //}

  // format needs to be like: "Thursday 25 January - 15:59"
  return formatUtc2HospitalDate(created, "dddd D MMMM - HH:mm");
};

// below REGEX_FILTERS no longer needd in favour of using itemType
// const REGEX_FILTERS = {
//   allergy: /(^Added .* allergy$)|(^Changed .* allergy to .*$)/,
//   task: /Set activity .* to .*/,
//   location: /^Set location to .*$/,
//   triage: /(^Set triage problem to ".*"$)|(^Set ATS Score to .*$)/,
// };

const parseNote = (item: Note) => ({
  ...item,
  type: "type" in item ? item.type : "simpleAndNote",
  role: item.role || item.createdByRole,
});

const parseActivity = (item: Activity) => ({
  ...item,
  type: "type" in item && item.type ? item.type : "simple",
  role: item.createdByRole,
});

type ObsReconstructionEntry = {
  reconstructed: Partial<Observation>;
  isLatest: boolean;
  observationId: string;
};

const buildObservationReconstructionMap = (activities: Activity[]): Map<string, ObsReconstructionEntry> => {
  const obsActivities = activities.filter(a => a.type === "observation" && a.info?.id);

  const groups = new Map<string, Activity[]>();
  for (const activity of obsActivities) {
    const obsId = (activity.info as any).id as string;
    const group = groups.get(obsId) || [];
    group.push(activity);
    groups.set(obsId, group);
  }

  const result = new Map<string, ObsReconstructionEntry>();

  for (const [obsId, group] of groups) {
    group.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());

    let reconstructed: Record<string, any> = {};

    for (let i = 0; i < group.length; i++) {
      const activity = group[i];
      const { id: _obsId, ...delta } = (activity.info || {}) as any;

      if (Object.keys(delta).length > 0) {
        reconstructed = _.merge({}, reconstructed, delta);
      }

      result.set(activity.id, {
        reconstructed: { ...reconstructed } as Partial<Observation>,
        isLatest: i === group.length - 1,
        observationId: obsId,
      });
    }
  }

  return result;
};

const parseObservationActivity = (
  item: Activity,
  reconstructionMap: Map<string, ObsReconstructionEntry>,
) => {
  const entry = reconstructionMap.get(item.id);
  const reconstructed = entry?.reconstructed;

  return {
    type: "observation" as const,
    role: item.createdByRole,
    id: item.id,
    text: item.text,
    createdBy: item.createdBy,
    created: item.created,
    createdByName: item.createdByName,
    createdByRole: item.createdByRole,
    lastModified: item.lastModified,
    actingAsName: item.actingAsName,
    summary: reconstructed
      ? <ObservationSummary mini align="flex-start" observations={[reconstructed as Observation]} />
      : undefined,
    observationId: entry?.observationId,
    isLatestForObservation: entry?.isLatest ?? false,
  };
};

const parseEvent = (item: Event): EventTimelineItem => {
  // @ts-ignore - event shape in flux, typing to be fixed
  const typeStr = item.messageType ?? item.event ?? "";
  let eventType: "event" | "encounter" | "outbound" | "inbound" = "event";

  if (typeStr.startsWith("encounter")) {
    eventType = "encounter";
  } else if (typeStr.startsWith("hl7-outbound")) {
    eventType = "outbound";
  } else if (typeStr.startsWith("hl7-inbound")) {
    eventType = "inbound";
  } else {
    //@ts-ignore
    eventType = "unknown";
  }

  return {
    ...item,
    type: eventType,
    role: "System",
    created: item.timestamp,
    createdByName: "System",
    createdByRole: "System",
    text: `Backend event: ${typeStr}`,
  };
};

const getLabelByActionType = (type: "simpleAndNote" | "simple" | "communication" | "allergy" | "location" | "triage" | "tasks" | "observation" | "comment" | "System" | "event" | "encounter" | "outbound" | "inbound" | "create" | "status") => {
  switch (type) {
    case "allergy":
    case "location":
    case "tasks":
    case "triage":
    case "communication":
    case "simple":
    case "System":
    case "observation":
    case "comment":
    case "event":
    case "create":
    case "status":
      return _.startCase(type);
    case "encounter":
      return "Encounter Event";
    case "outbound":
      return "Outbound Message";
    case "inbound":
      return "Inbound Message";
    case "simpleAndNote":
      return "Note";
  }
};

const getItemTime = (item: TimelineItem) =>
  parseISO(item.created as any).getTime();

export const PatientTimeLine = (props: PatientTimelineProps) => {
  const { encounter, filters, events } = props;
  const { settings } = useSettingsContext();
  const openEditObservation = useEditObservationModal();
  const [selectedEvent, setSelectedEvent] = useState<EventTimelineItem | null>(null);
  const [selectedEventDetails, setSelectedEventDetails] = useState<Event | null>(null);
  const [isEventDetailsLoading, setIsEventDetailsLoading] = useState(false);
  const [eventDetailsError, setEventDetailsError] = useState<string | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);

  const getObservationById = (id: string): Observation | undefined => {
    return encounter.observations?.find(obs => obs.id === id);
  };

  const reconstructionMap = buildObservationReconstructionMap(encounter.activities || []);

  const canEditObservation = (item: TimelineItem) => {
    if (item.type !== "observation") return false;
    const obsItem = item as any;
    if (!obsItem.isLatestForObservation) return false;

    const observationId = obsItem.observationId;
    if (!observationId) return false;

    const actualObservation = getObservationById(observationId);
    if (!actualObservation) return false;
    if (actualObservation.invalid) return false;

    const thresholdMinutes = (settings as any)?.encounter?.observations?.updateWindow || 5;
    const createdTime = parseISO(actualObservation.created);
    const now = new Date();
    const minutesSinceCreation = (now.getTime() - createdTime.getTime()) / (1000 * 60);

    return minutesSinceCreation <= thresholdMinutes;
  };

  const parsedItems = [
    ...(encounter.notes || []).map(parseNote),
    ...(encounter.activities || []).map(activity =>
      activity.type === "observation"
        ? parseObservationActivity(activity, reconstructionMap)
        : parseActivity(activity)
    ),
    ...(events || []).map(parseEvent),
  ] as TimelineItem[];
  // Example sub resource update and delete usage
  // PLEASE leave alone while we continue to test
  // const { useDelete, useUpdate } = useNoteService(encounter.id);
  // const { mutateAsync: deleteNote } = useDelete();
  // const { mutateAsync: updateNote } = useUpdate();

  const filteredItems = _.isEmpty(filters)
    ? parsedItems
    : parsedItems.filter((item) => {
      const role = (item.role || item.createdByRole || "other") as Practitioners | "other" | "System";
      return filterItem({ type: item.type, role }, filters);
    });
  const items = filteredItems.sort((a, b) => getItemTime(b) - getItemTime(a));

  const fetchEventDetails = useCallback(async (eventRowKey: string) => {
    setIsEventDetailsLoading(true);
    setEventDetailsError(null);
    try {
      const response = await global.client.get(`encounters/${encounter.id}/events/${eventRowKey}`);
      const data = (response as any)?.data;
      setSelectedEventDetails(data ?? null);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? "Failed to load event details.";
      setSelectedEventDetails(null);
      setEventDetailsError(String(msg));
    } finally {
      setIsEventDetailsLoading(false);
    }
  }, [encounter.id]);

  const handleEventClick = (event: EventTimelineItem) => {
    setSelectedEvent(event);
    setSelectedEventDetails(null);
    setIsEventModalOpen(true);
    // The list endpoint doesn't always include full message content anymore.
    // Fetch the full event payload by row key.
    fetchEventDetails(event.id);
  };

  const handleCloseEventModal = () => {
    setIsEventModalOpen(false);
    setSelectedEvent(null);
    setSelectedEventDetails(null);
    setEventDetailsError(null);
    setIsEventDetailsLoading(false);
  };

  const eventDetailsAny = selectedEventDetails as any;
  const eventListAny = selectedEvent as any;

  // Detail response can be missing fields that are present in the list response (e.g. `timestamp`),
  // and can use different keys (e.g. `eventId` vs `id`). Merge for display.
  const displayMessageType =
    eventDetailsAny?.messageType ?? eventDetailsAny?.event ?? eventListAny?.messageType ?? "";
  const displayId =
    eventDetailsAny?.id ?? eventDetailsAny?.eventId ?? eventListAny?.id ?? "";
  const displayTimestamp =
    eventDetailsAny?.timestamp ?? eventListAny?.timestamp ?? "";
  const displayCorrelationId =
    eventDetailsAny?.correlationId ?? eventListAny?.correlationId ?? "";
  const hasError = eventDetailsAny?.error != null;
  const displayPayload = hasError
    ? eventDetailsAny.error
    : (eventDetailsAny?.message ?? eventListAny?.message ?? "");

  const contentLabel = hasError ? "Error Content" : "Message Content";

  const messageForDisplay = (() => {
    const msg = displayPayload;
    if (msg === null || msg === undefined) return "";
    if (typeof msg === "string") return msg;
    try {
      return JSON.stringify(msg, null, 2);
    } catch {
      return String(msg);
    }
  })();

  return (
    <>
      <Timeline sx={styles.timeline} position="right">
        {items.map((item, index) => (
          <TimelineItem key={index}>
            <TimelineSeparator>
              <TimelineDot css={styles.dot} variant="outlined" />
              {index < items.length - 1 && (
                <TimelineConnector css={styles.connector} />
              )}
            </TimelineSeparator>

            <TimelineContent>
              <Box sx={{ display: "inline-flex", alignItems: "baseline" }} marginBottom={1}>
                <Text bold={true}>
                  {item.actingAsName ? item.actingAsName : item.createdByName}
                </Text>
                {item.actingAsName && (
                  <>
                    <Text
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      (signed in as {item.createdByName})
                    </Text>
                  </>
                )}
                <BulletPoint />
                <Text
                  display={"flex"}
                  flexDirection={"row"}
                  alignItems={"center"}
                  fontSize={12}
                >
                  <TimeIcon style={{ marginRight: 8 }} width={14} height={14} />{" "}
                  {formatTime(item.created)}
                </Text>
                <BulletPoint />
                <Box css={styles.roleBox}>{_.startCase(item.role)}</Box>
                <BulletPoint />
                <Box css={styles.typeBox}>{getLabelByActionType(item.type)}</Box>
              </Box>
              <Stack direction="column" gap="0.5rem">
                <Stack direction="row" alignItems="center" gap={1}>
                  <Stack direction="column" gap={0.5}>
                    {(item as Note).title ? (
                      <Text bold={true} sx={{ whiteSpace: "pre-wrap" }}>
                        {(item as Note).title}
                      </Text>
                    ) : null}
                    <Text sx={{ whiteSpace: "pre-wrap" }}>
                      {item.text}
                    </Text>
                  </Stack>
                  {(item.type === "event" || item.type === "encounter" || item.type === "outbound" || item.type === "inbound") && (
                    <Tooltip title="View event details">
                      <IconButton
                        size="small"
                        onClick={() => handleEventClick(item as EventTimelineItem)}
                        sx={{ ml: 1 }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>

                {item.type === "observation" && (
                  <Stack direction="row" alignItems="center">
                    {item.summary}
                    {canEditObservation(item) && (
                      <Tooltip title="Edit observation">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const entry = reconstructionMap.get(item.id);
                            if (entry) {
                              const observation = { ...entry.reconstructed, id: entry.observationId } as Observation;
                              openEditObservation({
                                encounter,
                                observation,
                                mode: "edit"
                              });
                            }
                          }}
                          sx={{ ml: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                )}
              </Stack>

              {/* See comment about subresource update and delete usage */}
              {/* <Button onClick={() => { updateNote({ id: item.id, payload: { text: "UPDATED" } }); }}>edit</Button>
              <Button onClick={() => { deleteNote(item.id); }}>delete</Button> */}
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>

      {/* Event Details Modal */}
      <Dialog
        open={isEventModalOpen}
        onClose={handleCloseEventModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Event Details: {displayMessageType}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <Text variant="h6" sx={{ mb: 1 }}>Event Information</Text>
              <Stack spacing={1}>
                <Text><strong>ID:</strong> {displayId}</Text>
                <Text><strong>Timestamp:</strong> {displayTimestamp}</Text>
                <Text><strong>Correlation ID:</strong> {displayCorrelationId}</Text>
                <Text><strong>Message Type:</strong> {displayMessageType}</Text>
              </Stack>
            </Box>
            <Box>
              <Text variant="h6" sx={{ mb: 1 }}>{contentLabel}</Text>
              {isEventDetailsLoading && (
                <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                  <CircularProgress size={18} />
                  <Text variant="caption">Loading full event details…</Text>
                </Stack>
              )}
              {eventDetailsError && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {eventDetailsError}
                </Alert>
              )}
              <JsonEditor
                label=""
                value={(() => {
                  try {
                    const parsedMessage = JSON.parse(messageForDisplay || "{}");
                    return JSON.stringify(parsedMessage, null, 2);
                  } catch (error) {
                    return messageForDisplay || "";
                  }
                })()}
                readOnly={true}
                height="300px"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEventModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
